import { z } from "zod";
import { momentSchema } from "./zod-utils";

export const TICKET_CUSTOM_FIELD_TYPES = ["shortText", "longText", "select", "multiselect", "imageUpload"] as const;

export const TicketCustomFieldTypeSchema = z.enum(TICKET_CUSTOM_FIELD_TYPES);

export type TicketCustomFieldType = z.infer<typeof TicketCustomFieldTypeSchema>;

export const TICKET_CUSTOM_FIELD_TYPE = TicketCustomFieldTypeSchema.enum;

const ShortTextOptionsSchema = z.object({
  type: z.literal(TICKET_CUSTOM_FIELD_TYPE.shortText),
  placeholder: z.string(),
  defaultValue: z.string(),
});

const LongTextOptionsSchema = z.object({
  type: z.literal(TICKET_CUSTOM_FIELD_TYPE.longText),
  placeholder: z.string(),
  defaultValue: z.string(),
});

const SelectOptionsSchema = z.object({
  type: z.literal(TICKET_CUSTOM_FIELD_TYPE.select),
  values: z.array(z.string()),
  defaultValue: z.string(),
});

const MultiselectOptionsSchema = z.object({
  type: z.literal(TICKET_CUSTOM_FIELD_TYPE.multiselect),
  values: z.array(z.string()),
  defaultValue: z.array(z.string()),
});

export const TicketCustomFieldOptionsSchema = z.discriminatedUnion("type", [
  ShortTextOptionsSchema,
  LongTextOptionsSchema,
  SelectOptionsSchema,
  MultiselectOptionsSchema,
]);

export type TicketCustomFieldOptions = z.infer<typeof TicketCustomFieldOptionsSchema>;

export const TICKET_CUSTOM_FIELD_TYPE_DEFAULTS: Record<TicketCustomFieldType, TicketCustomFieldOptions | null> = {
  shortText: {
    type: TICKET_CUSTOM_FIELD_TYPE.shortText,
    placeholder: "",
    defaultValue: "",
  },
  longText: {
    type: TICKET_CUSTOM_FIELD_TYPE.longText,
    placeholder: "",
    defaultValue: "",
  },
  select: {
    type: TICKET_CUSTOM_FIELD_TYPE.select,
    values: [],
    defaultValue: "",
  },
  multiselect: {
    type: TICKET_CUSTOM_FIELD_TYPE.multiselect,
    values: [],
    defaultValue: [],
  },
  imageUpload: null,
};

export const TicketCategoryCustomFieldBaseShape = {
  uuid: z.uuid(),
  label: z.string(),
  fieldType: TicketCustomFieldTypeSchema,
  isRequired: z.boolean(),
  options: TicketCustomFieldOptionsSchema.nullable(),
  sortIndex: z.string(),
};

export const TicketTemplateDefaultFieldSchema = z.object({
  label: z.string(),
  fieldType: TicketCustomFieldTypeSchema,
  isRequired: z.boolean(),
  options: TicketCustomFieldOptionsSchema.nullable(),
});

export type TicketTemplateDefaultField = z.infer<
  typeof TicketTemplateDefaultFieldSchema
>;

export const TicketCategoryCustomFieldSchema = z.object({
  ...TicketCategoryCustomFieldBaseShape,
  createdAt: momentSchema,
  updatedAt: momentSchema,
  deletedAt: momentSchema.nullable(),
});

export type TicketCategoryCustomField = z.infer<typeof TicketCategoryCustomFieldSchema>;

const ShortTextValueSchema = z.object({
  type: z.literal(TICKET_CUSTOM_FIELD_TYPE.shortText),
  value: z.string(),
});

const LongTextValueSchema = z.object({
  type: z.literal(TICKET_CUSTOM_FIELD_TYPE.longText),
  value: z.string(),
});

const SelectValueSchema = z.object({
  type: z.literal(TICKET_CUSTOM_FIELD_TYPE.select),
  value: z.string(),
});

const MultiSelectValueSchema = z.object({
  type: z.literal(TICKET_CUSTOM_FIELD_TYPE.multiselect),
  value: z.array(z.string()),
});

const ImageUploadValueSchema = z.object({
  type: z.literal(TICKET_CUSTOM_FIELD_TYPE.imageUpload),
  value: z.array(z.uuid()),
});

export const TicketCustomFieldValueSchema = z.discriminatedUnion("type", [
  ShortTextValueSchema,
  LongTextValueSchema,
  SelectValueSchema,
  MultiSelectValueSchema,
  ImageUploadValueSchema,
]);

export type TicketCustomFieldValue = z.infer<typeof TicketCustomFieldValueSchema>;

export const TicketCustomFieldsDataSchema = z.record(z.uuid(), TicketCustomFieldValueSchema);

export type TicketCustomFieldsData = z.infer<typeof TicketCustomFieldsDataSchema>;
